import React from "react";
import { X, CheckCircle2, AlertCircle } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useUploadProgress } from "@/contexts/uploadProgressContext";

/**
 * Floating upload progress panel.
 * Automatically appears when any useFileUpload or useBatchFileUpload call starts
 * and stacks multiple concurrent uploads.
 */
const UploadProgressPanel = () => {
  const ctx = useUploadProgress();

  if (!ctx) return null;

  const { uploads, dismissUpload } = ctx;
  const entries = Object.values(uploads);

  if (entries.length === 0) return null;

  const anyUploading = entries.some((u) => u.status === "uploading");

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 10) / 10} ${sizes[i]}`;
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds === Infinity || seconds <= 0)
      return "Calculating...";
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `${m}m ${s}s`;
  };

  return (
    <div
      className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-0 w-full max-w-md sm:w-[400px]"
      style={{ filter: "drop-shadow(0 8px 32px rgba(59,130,246,0.25))" }}
    >
      {/* Card */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          background: "linear-gradient(160deg, #1a2235 0%, #1e2a3a 100%)",
          border: "1.5px solid rgba(59,130,246,0.45)",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center gap-3 px-5 py-4"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
        >
          {/* Yvooa Y logo badge */}
          <div
            className="w-9 h-9 flex items-center justify-center rounded-xl shrink-0"
            style={{
              background: "linear-gradient(135deg, #3b82f6 0%, #F9C642 100%)",
            }}
          >
            <svg viewBox="0 0 100 100" style={{ width: 22, height: 22 }}>
              <polygon points="48,95 60,20 75,20" fill="#ffffff" />
              <polygon
                points="28,20 50,45 45,65"
                fill="#ffffff"
                opacity="0.9"
              />
            </svg>
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold text-sm leading-tight">
              Uploading{" "}
              {entries.length > 1 ? `${entries.length} Files` : "File"}
            </p>
            <p className="text-xs text-slate-400 mt-0.5">
              {entries.filter((u) => u.status === "success").length} of{" "}
              {entries.length} complete
            </p>
          </div>

          {/* Pulse indicator */}
          {anyUploading && (
            <span className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse shrink-0" />
          )}
        </div>

        {/* Upload list */}
        <div className="flex flex-col divide-y divide-white/5 max-h-[420px] overflow-y-auto">
          {entries.map((entry) => (
            <UploadRow
              key={entry.id}
              entry={entry}
              formatBytes={formatBytes}
              formatTime={formatTime}
              onDismiss={() => dismissUpload(entry.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * A single file row inside the panel.
 */
const UploadRow = ({ entry, formatBytes, formatTime, onDismiss }) => {
  const { fileName, fileSize, progress, status, uploadSpeed, remainingTime } =
    entry;

  const isFailed = status === "failed";
  const isSuccess = status === "success";
  const isUploading = status === "uploading";

  return (
    <div className="px-5 py-3.5 group relative">
      {/* File name row */}
      <div className="flex items-center gap-2 mb-2">
        {/* Status icon */}
        <div className="shrink-0">
          {isSuccess && (
            <CheckCircle2
              size={15}
              className="text-emerald-400"
              strokeWidth={2.5}
            />
          )}
          {isFailed && (
            <AlertCircle size={15} className="text-red-400" strokeWidth={2.5} />
          )}
          {isUploading && (
            <span className="block w-2 h-2 rounded-full bg-blue-400 animate-pulse mt-0.5" />
          )}
        </div>

        {/* File name */}
        <p
          className="flex-1 text-xs font-medium truncate"
          style={{ color: isFailed ? "#f87171" : "#e2e8f0" }}
          title={fileName}
        >
          {fileName}
        </p>

        {/* Action buttons */}
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={onDismiss}
            title="Dismiss"
            className={`w-5 h-5 flex items-center justify-center rounded-md hover:bg-white/10 transition-colors ${isFailed ? "text-slate-300 opacity-100" : "text-slate-500 hover:text-slate-300 opacity-0 group-hover:opacity-100"}`}
          >
            <X size={11} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Progress bar (hidden when done or failed) */}
      {(isUploading || isSuccess) && (
        <div className="mb-2">
          <div
            className="h-1.5 rounded-full overflow-hidden"
            style={{ background: "rgba(255,255,255,0.08)" }}
          >
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{
                width: `${progress}%`,
                background: isSuccess
                  ? "linear-gradient(90deg,#34d399,#10b981)"
                  : "linear-gradient(90deg,#3b82f6,#F9C642)",
              }}
            />
          </div>
        </div>
      )}

      {/* Failed bar */}
      {isFailed && (
        <div className="mb-2">
          <div
            className="h-1.5 rounded-full"
            style={{
              background:
                "linear-gradient(90deg,rgba(248,113,113,0.5),rgba(239,68,68,0.3))",
            }}
          />
        </div>
      )}

      {/* Stats row */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          {/* Percentage */}
          <span
            className="text-xs font-bold"
            style={
              isFailed
                ? { color: "#f87171" }
                : isSuccess
                  ? { color: "#34d399" }
                  : {
                      background:
                        "linear-gradient(90deg,#3b82f6 0%,#F9C642 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }
            }
          >
            {isFailed
              ? "Failed"
              : isSuccess
                ? "Done"
                : `${Math.round(progress)}%`}
          </span>

          {/* File size */}
          {fileSize > 0 && !isFailed && (
            <span className="text-xs text-slate-500">
              {formatBytes(fileSize)}
            </span>
          )}
        </div>

        {/* Speed / time remaining */}
        {isUploading && (
          <div className="text-right">
            {uploadSpeed > 0 && (
              <span className="text-xs text-slate-500 block">
                {formatBytes(uploadSpeed)}/s
              </span>
            )}
            <span className="text-xs text-slate-500">
              {remainingTime != null
                ? `${formatTime(remainingTime)} left`
                : "Calculating... left"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadProgressPanel;
